import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState } from "redux/store";
import { CriterionComponent } from "component/shared/criterion-component";
import { styles } from "./style";
import { Lead } from "model/lead";
import { SavedSearch } from "model/saved-search";
import { updateSavedSearch } from "redux/slice/saved-searches"
import { Dialog } from "component/shared/dialog";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { AppCard } from "component/shared/app-card";
import { parseKeywords, ParsedKeywords } from "shared/keyword-parser";

const mapStateToProps = (state: RootState) => {
	return {}
}

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	updateSavedSearch,
}, dispatch);

interface Props extends
	WithStyles<typeof styles>,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>
{
	lead: Lead;
	savedSearch: SavedSearch;
	openEditDialogue: Function;
}

interface State {
	showConfirmDialog: boolean;
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			showConfirmDialog: false,
		};
	}

	setConfirmDialog = (showConfirmDialog: boolean) => {
		this.setState({ showConfirmDialog })
	}

	saveSearch = (type: string) => {
		const inactive = true;
		let updatedSavedSearch = {
			...this.props.savedSearch,
		}
		if (type === "delete") {
			updatedSavedSearch.inactive = inactive;
		} else if (type ==="emailAlert") {
			updatedSavedSearch.recieveEmails = !updatedSavedSearch.recieveEmails;
		}
		this.props.updateSavedSearch({savedSearch: updatedSavedSearch});
	}

	renderConfirmationDialog = () => {
		const { classes = {} } = this.props;
		return (
			<Dialog
				open={this.state.showConfirmDialog}
				onClose={() => this.setConfirmDialog(false)}
			>
				<Mui.DialogTitle>Delete Saved Search</Mui.DialogTitle>
				<Mui.DialogContent className={classes.dialogContent}>
					<p>Are you sure you want to delete this search?</p>
				</Mui.DialogContent>
				<Mui.DialogActions className={classes.dialogActions}>
					<Mui.Button
						variant="outlined"
					color={"primary"}
						onClick={() => this.setConfirmDialog(false)}
					>
						No
					</Mui.Button>
					<Mui.Button
						variant="contained"
						color={"primary"}
						onClick={() => {
							this.saveSearch("delete");
							this.setConfirmDialog(false);
						}}
					>
						Yes
					</Mui.Button>
				</Mui.DialogActions>
			</Dialog>
		);
	};

	public render() {
		const { savedSearch, classes = {} } = this.props;
		return (
			<>
				{this.renderConfirmationDialog()}
				<AppCard className={classes.cardSpacing}>
					<Mui.CardHeader
						component={() => {
							return (
								<Mui.Grid container justifyContent="space-between" alignItems="center" className={classes.cardHeader}>
									<Mui.Grid item>
										{!!savedSearch.resultCount && savedSearch.resultCount > 0 ? (
											<Mui.Link href={savedSearch.resultsUrl} target="_blank" color="primary" underline="none">
												<Mui.Typography variant="h4">
													{savedSearch.label} ({savedSearch.resultCount})
												</Mui.Typography>
											</Mui.Link>
										) : (
											<Mui.Typography variant="h4">{savedSearch.label}</Mui.Typography>
										)}
									</Mui.Grid>
									<Mui.Grid item>
										<Mui.Typography>{`Saved ${savedSearch.createdOn?.format("MMMM D, YYYY")}`}</Mui.Typography>
									</Mui.Grid>
								</Mui.Grid>
							);
						}}
					/>
					<Mui.CardContent>
						<Mui.Grid container spacing={2} direction="row">
							<Mui.Grid item xs={12}>
								<CriterionComponent criteria={savedSearch.criteria} />
							</Mui.Grid>
							<Mui.Grid item xs={12}>
								<Mui.FormControlLabel
									label="Email Alerts"
									labelPlacement={"start"}
									className={classes.emailAlertsFormControl}
									classes={{ label: classes.emailAlertsLabel }}
									control={
										<Mui.Switch
											checked={savedSearch.recieveEmails}
											onChange={() => {
												this.saveSearch("emailAlert");
											}}
											color="secondary"
										/>
									}
								/>
							</Mui.Grid>
							{(() => {
								const keywordsCriterion = savedSearch.criteria?.find(c => c.name === "keywords");
								let keywords: ParsedKeywords | undefined;
								if (keywordsCriterion) {
									if (typeof keywordsCriterion.value === "string") {
										keywords = parseKeywords(keywordsCriterion.value);
									} else if (Array.isArray(keywordsCriterion.value) && keywordsCriterion.value.length > 0 && Array.isArray((keywordsCriterion.value as any)[0])) {
										keywords = {
											sets: (keywordsCriterion.value as unknown as string[][]).map(set => ({
												keywords: Array.isArray(set) ? set : [set]
											}))
										};
									}
								}
								if (!keywords || keywords.sets.length === 0) return null;
								return (
									<>
										<Mui.Grid item xs={12}>
											<Mui.Divider />
										</Mui.Grid>
										<Mui.Grid item xs={12}>
											<Mui.Typography className={classes.keywordsLabel}>Keywords:</Mui.Typography>
											{keywords.sets.map((set, setIndex) => (
												<React.Fragment key={setIndex}>
													{setIndex > 0 && (
														<Mui.Typography component="span" className={classes.keywordsSeparator}>&</Mui.Typography>
													)}
													{set.keywords.map((keyword, keywordIndex) => (
														<Mui.Chip key={keywordIndex} className={classes.keywordChip} label={keyword} />
													))}
												</React.Fragment>
											))}
										</Mui.Grid>
									</>
								);
							})()}
						</Mui.Grid>
					</Mui.CardContent>
					<Mui.CardActions>
						<Mui.Grid container justifyContent="space-between" alignItems="center">
							<Mui.Grid item>
								<Mui.Button
									color="primary"
									onClick={() => this.props.openEditDialogue()}
								>
									Edit
								</Mui.Button>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.IconButton
									onClick={() => this.setConfirmDialog(true)}
								>
									<FeatherIcon>
										<Icons.Trash />
									</FeatherIcon>
								</Mui.IconButton>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.CardActions>
				</AppCard>
			</>
		);
	}
}

export const SavedSearchCard = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
